"use client";

import { useTranslations } from "next-intl";
import { motion } from "motion/react";
import { SectionShell, EditorialTitle, GradientText, RevealOnScroll, useUserYear } from "./_primitives";

export default function ExclusivitySection() {
    const t = useTranslations();
    const year = useUserYear();

    const grid = [
        { name: "React", state: "taken" },
        { name: "Python IA", state: "taken" },
        { name: "Rust", state: "you" },
        { name: "Next.js", state: "taken" },
        { name: "Go backend", state: "available" },
        { name: "TypeScript", state: "taken" },
        { name: "Swift / iOS", state: "available" },
        { name: "Kubernetes", state: "available" },
        { name: "Tailwind", state: "taken" },
        { name: "Data Eng.", state: "available" },
        { name: "Solidity", state: "available" },
        { name: "Vue.js", state: "available" },
    ];

    const items = [1, 2, 3, 4].map((i) => ({
        num: String(i).padStart(2, "0"),
        bold: t(`joinInstructor_excl_item${i}_bold`),
        text: t(`joinInstructor_excl_item${i}_text`),
    }));

    return (
        <SectionShell label={t("joinInstructor_excl_marker")}>
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-start">
                <div>
                    <RevealOnScroll>
                        <p className="text-[11px] uppercase tracking-[0.28em] text-primary mb-6">
                            {t("joinInstructor_excl_eyebrow")}
                        </p>
                        <EditorialTitle className="text-4xl sm:text-5xl md:text-7xl">
                            {t("joinInstructor_excl_title_part1")}
                            <br />
                            <GradientText>{t("joinInstructor_excl_title_emphasis")}</GradientText>
                        </EditorialTitle>
                        <p
                            className="mt-8 text-xl text-gray-600 dark:text-white/70 italic"
                            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                        >
                            {t("joinInstructor_excl_subtitle_part1")}{" "}
                            <span className="text-gray-900 dark:text-white not-italic font-semibold">
                                {t("joinInstructor_excl_subtitle_emphasis")}
                            </span>
                            {t("joinInstructor_excl_subtitle_part2")}
                        </p>
                    </RevealOnScroll>

                    <div className="mt-12 space-y-7">
                        {items.map((it, i) => (
                            <RevealOnScroll key={it.num} delay={i * 0.08}>
                                <div className="flex gap-5">
                                    <span className="font-mono text-xs text-primary mt-1 shrink-0">
                                        {it.num}
                                    </span>
                                    <p className="text-gray-700 dark:text-white/85 leading-relaxed">
                                        <span className="font-semibold text-gray-900 dark:text-white">
                                            {it.bold}
                                        </span>{" "}
                                        {it.text}
                                    </p>
                                </div>
                            </RevealOnScroll>
                        ))}
                    </div>
                </div>

                <RevealOnScroll delay={0.15}>
                    <div className="rounded-3xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/[0.03] p-6">
                        <div className="flex justify-between items-center mb-5 text-[10px] uppercase tracking-[0.25em] text-gray-400 dark:text-white/40 font-mono">
                            <span>{t("joinInstructor_excl_grid_title", { year })}</span>
                            <span className="flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                                {t("joinInstructor_excl_grid_live")}
                            </span>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            {grid.map((cell, i) => (
                                <motion.div
                                    key={cell.name + i}
                                    initial={{ opacity: 0, scale: 0.92 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.4, delay: i * 0.04 }}
                                    className={`relative rounded-xl h-16 md:h-20 flex items-center justify-center text-sm font-medium transition-all ${
                                        cell.state === "you"
                                            ? "bg-gradient-to-br from-[#FF7A1A] to-[#E51578] text-white shadow-[0_0_24px_rgba(232,81,29,0.2)]"
                                            : cell.state === "taken"
                                            ? "border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/[0.03] text-gray-500 dark:text-white/55 line-through"
                                            : "border border-dashed border-primary/40 text-primary"
                                    }`}
                                >
                                    {cell.state === "you" ? (
                                        <span className="absolute -top-2 -right-2 px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] rounded-full bg-white dark:bg-[#0E161D] text-primary font-bold">
                                            {t("joinInstructor_excl_you")}
                                        </span>
                                    ) : null}
                                    {cell.name}
                                </motion.div>
                            ))}
                        </div>
                        <div className="flex flex-wrap gap-4 mt-5 text-[10px] uppercase tracking-[0.2em] text-gray-400 dark:text-white/40 font-mono">
                            <span className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/[0.03]" />
                                {t("joinInstructor_excl_legend_taken")}
                            </span>
                            <span className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded border border-dashed border-primary/40" />
                                {t("joinInstructor_excl_legend_available")}
                            </span>
                            <span className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded bg-primary" />
                                {t("joinInstructor_excl_legend_you")}
                            </span>
                        </div>
                    </div>
                </RevealOnScroll>
            </div>
        </SectionShell>
    );
}
